import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';

/**
 * VIEW_TO_PATH maps old-style view keys (used by `onNavigate('view-key')`)
 * to URL paths under the react-router regime.
 *
 * This is the **compatibility bridge** — child components keep calling
 * `onNavigate('dashboard')` and it resolves to `navigate('/app')`.
 *
 * Parameterized views like `chat-{replicaId}` are handled dynamically
 * in the hook body (see prefix matching below).
 */
const VIEW_TO_PATH: Record<string, string> = {
    // Dashboard root
    'dashboard': '/app',
    'home': '/',

    // Dashboard sub-views
    'my-replica': '/app/my-replica',
    'company-replicas': '/app/company-replicas',
    'teams': '/app/teams',
    'hierarchy': '/app/org-chart',
    'org-chart': '/app/org-chart',
    'marketplace': '/app/marketplace',
    'replica-directory': '/app/replica-directory',
    'create-replica': '/app/create-replica',
    'escalation-queue': '/app/escalation-queue',
    'task-management': '/app/task-management',
    'training-rounds': '/app/training-rounds',
    'deploy': '/app',
    'governance': '/app/governance',
    'create-team': '/app/create-team',
    'brain': '/app/brain',
    'employee-accounts': '/app/employee-accounts',
    'cognitive-merge': '/app/cognitive-merge',

    // Command Center (Operate Mode)
    'command-center': '/app/work',
    'work': '/app/work',
    'daily-routines': '/app/work',
    'work-mode': '/app/work',
    'computer-control': '/app/computer-control',
    'messages': '/app/messages',

    // Social
    'social': '/app/social',
    'feed-synthesis': '/app/feed-synthesis',

    // Briefcase
    'briefcase': '/app/briefcase',

    // Data & Sovereignty
    'data': '/app/data',
    'my-data': '/app/data',
    'permissions': '/app/permissions',
    'audit-logs': '/app/audit-logs',

    // Settings & Infrastructure
    'settings': '/app/settings',
    'identity-verification': '/app/identity-verification',
    'integrations': '/app/integrations',
    'billing': '/app/billing',
    'new-workspace': '/app/new-workspace',
    'join-workspace': '/app/join-workspace',

    // Public / Legal pages (accessible from dashboard too)
    'pricing': '/pricing',
    'privacy': '/privacy',
    'trust': '/trust',
    'contact': '/contact',
    'whitepaper': '/whitepaper',
    'manifesto': '/manifesto',
    'squircle-demo': '/squircle-demo',
    'payment-success': '/payment-success',
    'admin': '/admin',
};

/**
 * Parameterized prefixes that encode an ID into the path.
 * e.g. `onNavigate('chat-abc123')` → `/app/chat/abc123`
 */
const PARAM_PREFIXES = [
    { prefix: 'social-chat-', pathTemplate: '/app/social-chat/' },
    { prefix: 'cognitive-setup-', pathTemplate: '/app/cognitive-setup/' },
    { prefix: 'cognitive-lab-', pathTemplate: '/app/cognitive-lab/' },
    { prefix: 'skill-library-', pathTemplate: '/app/skill-library/' },
    { prefix: 'training-', pathTemplate: '/app/training/' },
    { prefix: 'edit-replica-', pathTemplate: '/app/edit-replica/' },
    { prefix: 'chat-', pathTemplate: '/app/chat/' },
    { prefix: 'tools-', pathTemplate: '/app/tools/' },
    { prefix: 'identity-', pathTemplate: '/app/identity/' },
    { prefix: 'mcp-tools-', pathTemplate: '/app/mcp-tools/' },
    { prefix: 'memory-', pathTemplate: '/app/memory/' },
    { prefix: 'employment-', pathTemplate: '/app/employment/' },
    { prefix: 'team-', pathTemplate: '/app/team/' },
];

/**
 * Resolve a legacy view key to a URL path.
 * Exported so AppContent can use it without the hook (outside Router context).
 */
export function viewKeyToPath(viewKey: string): string {
    // 1. Check static map
    const staticPath = VIEW_TO_PATH[viewKey];
    if (staticPath) return staticPath;

    // 2. Check parameterized prefixes (order matters — longest prefix first)
    for (const { prefix, pathTemplate } of PARAM_PREFIXES) {
        if (viewKey.startsWith(prefix)) {
            const id = viewKey.slice(prefix.length);
            return `${pathTemplate}${id}`;
        }
    }

    // 3. Fallback: unknown view → dashboard
    console.warn(`[useAppNavigate] Unknown view key: "${viewKey}", falling back to /app`);
    return '/app';
}

/**
 * Reverse: resolve a URL path back to a legacy view key.
 * Used by DashboardApp to read the current route and pass it to renderView().
 */
export function pathToViewKey(pathname: string): string {
    // Strip /app prefix for dashboard routes
    if (pathname.startsWith('/app/') || pathname === '/app') {
        const subpath = pathname === '/app' ? '' : pathname.slice(5); // remove '/app/'

        if (!subpath) return 'dashboard';

        // Check parameterized paths
        for (const { prefix, pathTemplate } of PARAM_PREFIXES) {
            const appRelativeTemplate = pathTemplate.slice(5); // remove '/app/'
            if (subpath.startsWith(appRelativeTemplate)) {
                const id = subpath.slice(appRelativeTemplate.length);
                return `${prefix}${id}`;
            }
        }

        // Check static reverse map
        for (const [viewKey, path] of Object.entries(VIEW_TO_PATH)) {
            if (path === pathname) return viewKey;
        }

        // Unknown sub-path → dashboard
        return 'dashboard';
    }

    // Public pages
    for (const [viewKey, path] of Object.entries(VIEW_TO_PATH)) {
        if (path === pathname) return viewKey;
    }

    return 'home';
}

/**
 * useAppNavigate — drop-in replacement for the old `onNavigate` callback.
 *
 * Usage:
 * ```tsx
 * const navigate = useAppNavigate();
 * navigate('dashboard');       // → /app
 * navigate('chat-abc123');     // → /app/chat/abc123
 * navigate('privacy');         // → /privacy
 * ```
 */
export function useAppNavigate() {
    const navigate = useNavigate();

    return useCallback((viewKey: string) => {
        const path = viewKeyToPath(viewKey);
        navigate(path);
    }, [navigate]);
}
